import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { resetPassword } from '../../services/api';
import PasswordStrengthMeter from '../common/PasswordStrengthMeter';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Eye, EyeOff, CheckCircle2, AlertCircle, ArrowRight } from 'lucide-react';
import '../auth/AuthForms.scss';

const PasswordResetConfirm = () => {
  const { t } = useTranslation(['auth', 'common']);
  // Fixed param mismatch: App.js uses :code, earlier code used token
  const { userId, code } = useParams();
  const navigate = useNavigate();
  const [resetError, setResetError] = useState(null);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  const PasswordResetSchema = Yup.object().shape({
    new_password: Yup.string()
      .min(8, t('errors.password_min'))
      .matches(/[a-z]/, t('errors.password_min'))
      .matches(/[A-Z]/, t('errors.password_min'))
      .matches(/[0-9]/, t('errors.password_min'))
      .required(t('errors.password_required')),
    confirm_password: Yup.string()
      .oneOf([Yup.ref('new_password'), null], t('errors.password_match'))
      .required(t('errors.password_confirm')),
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 600);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setResetError(null);
      // Use code from params (synced with App.js)
      await resetPassword(userId, code, values.new_password);
      setResetSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      setResetError(t('reset_password.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="auth-loading">
        <motion.div 
          animate={{ scale: [1, 1.1, 1], opacity: [0.5, 1, 0.5] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="loading-logo"
        >
          FOTH
        </motion.div>
      </div>
    );
  }

  return (
    <div className="auth-page editorial-split">
      <div className="auth-editorial-side">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.5 }}
            className="editorial-bg-overlay"
          />
          <div className="editorial-content">
            <Link to="/" className="auth-logo-inverse">FOTH</Link>
            <div className="editorial-text">
               <motion.h1
                 initial={{ opacity: 0, y: 20 }}
                 animate={{ opacity: 1, y: 0 }}
                 transition={{ duration: 0.8 }}
               >
                 {t('reset_password.editorial.title')}
               </motion.h1>
               <motion.p
                 initial={{ opacity: 0 }}
                 animate={{ opacity: 1 }}
                 transition={{ delay: 0.3 }}
               >
                 {t('reset_password.editorial.text')}
               </motion.p>
            </div>
          </div>
      </div>

      <div className="auth-form-side">
        <div className="auth-container">
          <AnimatePresence mode="wait">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="auth-card-modern"
            >
              <div className="auth-header">
                <h2 className="auth-title">{t('reset_password.title')}</h2>
                <p className="auth-subtitle">{t('reset_password.subtitle')}</p>
              </div>
              
              <div className="auth-body">
                {resetSuccess ? (
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                  >
                    <div className="alert-premium success column-layout">
                      <CheckCircle2 size={32} />
                      <p>{t('reset_password.success')}</p>
                    </div>
                  </motion.div>
                ) : (
                  <>
                    {resetError && (
                      <div className="alert-premium error" role="alert">
                        <AlertCircle size={18} /> {resetError}
                      </div>
                    )}
                    
                    <Formik
                      initialValues={{ new_password: '', confirm_password: '' }}
                      validationSchema={PasswordResetSchema}
                      onSubmit={handleSubmit}
                    >
                      {({ isSubmitting, values }) => (
                        <Form className="auth-form">
                          <div className="form-group-full">
                            <label className="form-label">{t('reset_password.form.new_password')}</label>
                            <div className="input-premium-wrapper">
                              <Lock size={18} className="input-icon" />
                              <Field
                                type={showPassword ? 'text' : 'password'}
                                name="new_password"
                                className="form-control-premium"
                                placeholder={t('reset_password.form.placeholder.new_password')}
                              />
                              <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowPassword(!showPassword)}
                              >
                                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                              </button>
                            </div>
                            <PasswordStrengthMeter password={values.new_password} />
                            <ErrorMessage name="new_password" component="div" className="error-message" />
                          </div>

                          <div className="form-group-full">
                            <label className="form-label">{t('reset_password.form.confirm_password')}</label>
                            <div className="input-premium-wrapper">
                              <Lock size={18} className="input-icon" />
                              <Field
                                type={showPassword ? 'text' : 'password'}
                                name="confirm_password"
                                className="form-control-premium"
                                placeholder={t('reset_password.form.placeholder.confirm_password')}
                              />
                            </div>
                            <ErrorMessage name="confirm_password" component="div" className="error-message" />
                          </div>

                          <motion.button
                            type="submit"
                            className="auth-submit-btn-premium"
                            disabled={isSubmitting}
                            whileTap={{ scale: 0.98 }}
                          >
                            {isSubmitting ? (
                              <span className="btn-spinner" />
                            ) : (
                              <>{t('reset_password.submit')} <ArrowRight size={18} /></>
                            )}
                          </motion.button>
                        </Form>
                      )}
                    </Formik>
                  </>
                )}
              </div>
              
              <div className="auth-footer">
                <p><Link to="/login">{t('forgot_password.footer.back_to_login')}</Link></p>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default PasswordResetConfirm;